// Syntax highlighting theme of code blocks, selectable per app theme mode (dark themes in dark mode, light ones
// in light mode) and remembered in localStorage. The Prism theme stylesheet is swapped in a <link> element.
import { computed, ref, watch } from "vue";
import shades_of_purple from "prism-themes/themes/prism-shades-of-purple.css?url";
import one_dark from "prism-themes/themes/prism-one-dark.css?url";
import dracula from "prism-themes/themes/prism-dracula.css?url";
import nord from "prism-themes/themes/prism-nord.css?url";
import night_owl from "prism-themes/themes/prism-night-owl.css?url";
import vsc_dark_plus from "prism-themes/themes/prism-vsc-dark-plus.css?url";
import material_dark from "prism-themes/themes/prism-material-dark.css?url";
import gruvbox_dark from "prism-themes/themes/prism-gruvbox-dark.css?url";
import one_light from "prism-themes/themes/prism-one-light.css?url";
import ghcolors from "prism-themes/themes/prism-ghcolors.css?url";
import vs from "prism-themes/themes/prism-vs.css?url";
import material_light from "prism-themes/themes/prism-material-light.css?url";
import gruvbox_light from "prism-themes/themes/prism-gruvbox-light.css?url";
import solarized_light from "prismjs/themes/prism-solarizedlight.css?url";

type Mode = "dark" | "light";

export interface CodeTheme {
  id: string;
  label: string;
  url: string;
  // Keeps the app's own code background instead of the theme's
  app_bg?: boolean;
  // Representative colors for the theme picker, from the theme's stylesheet: its code background, and its
  // keyword, string, function and number colors (or a related token where one of these repeats another)
  bg: string;
  colors: string[];
}

// The first theme of each mode is the default
export const CODE_THEMES: Record<Mode, CodeTheme[]> = {
  dark: [
    { id: "shades-of-purple", label: "Shades of Purple", url: shades_of_purple, app_bg: true,
      bg: "#1e1e3f", colors: ["#ff9d00", "#a5ff90", "#fad000", "#ff628c"] },
    { id: "one-dark", label: "One Dark", url: one_dark,
      bg: "hsl(220, 13%, 18%)", colors: ["hsl(286, 60%, 67%)", "hsl(95, 38%, 62%)", "hsl(207, 82%, 66%)", "hsl(29, 54%, 61%)"] },
    { id: "dracula", label: "Dracula", url: dracula,
      bg: "#282a36", colors: ["#8be9fd", "#50fa7b", "#f1fa8c", "#bd93f9"] },
    { id: "nord", label: "Nord", url: nord,
      bg: "#2e3440", colors: ["#81a1c1", "#a3be8c", "#88c0d0", "#b48ead"] },
    { id: "night-owl", label: "Night Owl", url: night_owl,
      bg: "#011627", colors: ["#7fdbca", "#addb67", "#82aaff", "#f78c6c"] },
    { id: "vsc-dark-plus", label: "VS Code Dark+", url: vsc_dark_plus,
      bg: "#1e1e1e", colors: ["#569cd6", "#ce9178", "#dcdcaa", "#b5cea8"] },
    { id: "material-dark", label: "Material Dark", url: material_dark,
      bg: "#2f2f2f", colors: ["#c792ea", "#a5e844", "#80cbc4", "#fd9170"] },
    { id: "gruvbox-dark", label: "Gruvbox Dark", url: gruvbox_dark,
      bg: "#1d2021", colors: ["#fb4934", "#b8bb26", "#fabd2f", "#d3869b"] },
  ],
  light: [
    { id: "one-light", label: "One Light", url: one_light,
      bg: "hsl(230, 1%, 98%)", colors: ["hsl(301, 63%, 40%)", "hsl(119, 34%, 47%)", "hsl(221, 87%, 60%)", "hsl(35, 99%, 36%)"] },
    { id: "github", label: "GitHub", url: ghcolors,
      bg: "#ffffff", colors: ["#00a4db", "#e3116c", "#9a050f", "#36acaa"] },
    { id: "vs", label: "Visual Studio", url: vs,
      bg: "#ffffff", colors: ["#0000ff", "#a31515", "#2b91af", "#008000"] },
    { id: "material-light", label: "Material Light", url: material_light,
      bg: "#fafafa", colors: ["#7c4dff", "#f6a434", "#39adb5", "#f76d47"] },
    { id: "gruvbox-light", label: "Gruvbox Light", url: gruvbox_light,
      bg: "#f9f5d7", colors: ["#9d0006", "#797403", "#b57614", "#8f3f71"] },
    { id: "solarized-light", label: "Solarized Light", url: solarized_light,
      bg: "#fdf6e3", colors: ["#859900", "#2aa198", "#b58900", "#268bd2"] },
  ],
};

const LS_KEY = (mode: Mode) => `code_theme_${mode}`;

const read_ls = (mode: Mode) => {
  try {
    return localStorage.getItem(LS_KEY(mode));
  }
  catch {
    return null;
  }
};

// The app's current theme mode, following data-bs-theme on <html> (which also resolves "system")
const read_mode = (): Mode => (document.documentElement.getAttribute("data-bs-theme") === "light" ? "light" : "dark");
export const app_mode = ref<Mode>(read_mode());
new MutationObserver(() => { app_mode.value = read_mode(); })
  .observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });

const selected = ref<Record<Mode, string | null>>({ dark: read_ls("dark"), light: read_ls("light") });

export const code_themes = computed(() => CODE_THEMES[app_mode.value]);

// Selected theme of the current mode; an unknown id falls back to the mode's default
export const code_theme = computed<string>({
  get: () => {
    const list = code_themes.value;
    return list.find(t => t.id === selected.value[app_mode.value])?.id ?? list[0].id;
  },
  set: (id) => {
    selected.value[app_mode.value] = id;
    try {
      localStorage.setItem(LS_KEY(app_mode.value), id);
    }
    catch {
      // Not remembered, e.g. with storage blocked
    }
  },
});

// Inserted first in <head>, so the app's own code block styles win over the theme on equal specificity
const link = document.createElement("link");
link.rel = "stylesheet";
document.head.prepend(link);

// The theme's code background, read from a hidden probe element once its stylesheet has loaded
const theme_background = () => {
  const probe = document.createElement("pre");
  probe.className = "language-none";
  probe.style.cssText = "position: absolute; visibility: hidden;";
  document.body.appendChild(probe);
  const bg = getComputedStyle(probe).backgroundColor;
  probe.remove();
  return bg;
};

const apply = () => {
  const theme = code_themes.value.find(t => t.id === code_theme.value)!;
  const root = document.documentElement.style;
  link.onload = () => {
    if (theme.app_bg) root.removeProperty("--code-block-bg");
    else root.setProperty("--code-block-bg", theme_background());
  };
  link.href = theme.url;
  // Header band and text over the code background
  root.setProperty("--code-block-header-bg", app_mode.value === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.05)");
  root.setProperty("--code-block-header-color", app_mode.value === "light" ? "var(--bs-gray-700)" : "var(--bs-prismjs-btn-color)");
};

watch(code_theme, apply, { immediate: true });
